import {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
  useController,
} from 'react-hook-form';
import { TextInput as CapsuleTextInput, Text } from '@ebh/capsule';
import { Interpolation, Theme } from '@emotion/react';
import { ReactNode } from 'react';
import { InfoIcon } from '@ebh/capsule/icons';
import styles from './styles';

interface TextInputProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  autocomplete?: string;
  label?: string;
  placeholder?: string;
  start?: ReactNode;
  end?: ReactNode;
  style?: Interpolation<Theme>;
  type?: 'text' | 'password' | 'email' | 'number';
  showIcon?: boolean;
  error?: boolean;
  helperText?: string;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;

  onFocusEvent?: () => void;
  onClickEvent?: () => void;
}

const TextInput = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  start,
  end,
  style,
  type = 'text',
  showIcon,
  error: externalError,
  helperText,
  rules,
  onFocusEvent,
  onClickEvent,
}: TextInputProps<TFieldValues>) => {
  const {
    field,
    fieldState: { invalid },
    formState: { errors } = {},
  } = useController({
    name,
    control,
    rules,
  });

  const isError = externalError ?? invalid;
  const message = helperText ?? (errors?.[field?.name]?.message as string);

  return (
    <div css={styles.container}>
      <CapsuleTextInput
        error={isError}
        type={type}
        css={[styles.input, style]}
        hint={!showIcon ? message : ''}
        label={label}
        placeholder={placeholder}
        start={start}
        data-testid="M1nO4pQ7rS0tU3vW6xY-"
        {...field}
        onClick={() => onClickEvent?.()}
        onFocus={() => onFocusEvent?.()}
        onChange={(e) => field.onChange(e.target.value)}
        end={end}
      />

      {message && showIcon && (
        <div css={styles.error} data-testid="B9cD2eF5gH8iJ1kL4mN-">
          <InfoIcon color="error-400" style={{ height: 20 }} />
          <Text variant="body-1" color="error">
            {message}
          </Text>
        </div>
      )}
    </div>
  );
};

export default TextInput;
